import { IVeriMapComponent } from "@directives/_map/veriMap/veriMapComponent/veriMap.component";
import { IVeriTableHeader, IVeriTableRow, IVeriTableRowCell } from "@directives/_veri/veriTable/veriTable.component";
import { VeriTableService } from "@directives/_veri/veriTable/veriTable.service";
import { SortType } from "@enums/SortType";
import { ICombinedPreferences } from "@interfaces/ICombinedPreferences";
import { MapService } from "@services/map.service";
import { PreferencesService } from "@services/preferences.service";
import { TranslationService } from "@services/translation.service";
import { UnitConversionService, UnitConversionType } from "@services/unitConversion.service";
import { VeriDate } from "@services/veriDate.service";
import { RouteEndCondition } from "@tms/_enums/RouteEndCondition";
import { RouteStartCondition } from "@tms/_enums/RouteStartCondition";
import { RouteStatus } from "@tms/_enums/RouteStatus";
import { RouteVisitStatus } from "@tms/_enums/RouteVisitStatus";
import { VisitMatchOptions } from "@tms/_enums/VisitMatchOptions";
import { IPlannedRoute } from "@tms/_models/IPlannedRoute";
import { IPlannedRouteVisit } from "@tms/_models/IPlannedRouteVisit";
import { IRouteExpiryRules, IRouteMatchingRules, ITmsRouteRules, IVisitMatchingRules } from "@tms/_models/ITmsRouteRules";
import { IPlannedRouteDetail } from "@tms/_services/planned-route.service";
import { ILatLngLiteral, IPolyline } from "../../../../../../scripts/typings/google.maps.angular";

export type RouteMatchingRuleLabels = Record<string, string>;

export interface IPlannedRouteVisitListValidation {
    valid: boolean;
    reasonMap: { [reason: number]: boolean };
}

export enum PlannedRouteVisitListValidationReason {
    NoVisits,
    VisitMissingLocation,
    VisitArrivalTimeBeforePrevious,
    VisitSameLocationAsPrevious,
    NoArrivalTime,
    NoDepartureTime,
    NoArrivalAndDepartureTime
}

export class SchedulingService {

    preferences: ICombinedPreferences;

    static $inject = ["veriDate", "mapService", "veriTableService", "translationService", "unitConversionService", "preferencesService"];

    constructor(
        private readonly veriDate: VeriDate,
        private readonly mapService: MapService,
        private readonly veriTableService: VeriTableService,
        private readonly translationService: TranslationService,
        private readonly unitConversionService: UnitConversionService,
        private readonly preferencesService: PreferencesService) {

        this.preferencesService.getAllPreferences().then(preferences => {

            this.preferences = preferences;
        });
    }

    getStartRouteMatchingRuleLabels(): RouteMatchingRuleLabels {
        return {
            ["StartRouteOnJourneyStart"]: this.translationService.t("SchedulingService.StartRouteOnJourneyStart"),
            ["StartRouteOnDepartAnyVisitLocation"]: this.translationService.t("SchedulingService.StartRouteOnDepartAnyVisitLocation"),
            ["StartRouteOnDepartStartingLocation"]: this.translationService.t("SchedulingService.StartRouteOnDepartStartingLocation")
        }
    }

    getEndRouteMatchingRuleLabels(): RouteMatchingRuleLabels {
        return {
            ["EndRouteOnAllVisitsArrived"]: this.translationService.t("SchedulingService.EndRouteOnAllVisitsArrived"),
            ["EndRouteOnArriveAtEndingLocation"]: this.translationService.t("SchedulingService.EndRouteOnArriveAtEndingLocation")
        }
    }

    createDefaultTmsRouteRules(): ITmsRouteRules {
        const newRules: ITmsRouteRules = {
            VisitMatchingRules: this.createDefaultVisitMatchingRules(),
            RouteMatchingRules: this.createDefaultRouteMatchingRules(),
            RouteExpiryRules: this.createDefaultRouteExpiryRules()
        };

        return newRules;
    }

    createDefaultRouteMatchingRules(): IRouteMatchingRules {
        const newRules: IRouteMatchingRules = {
            StartRouteOnDepartStartingLocation: false,
            StartRouteOnArriveAnyVisitLocation: false,
            StartRouteOnDepartAnyVisitLocation: false,
            StartRouteOnJourneyStart: false,
            EndRouteOnAllVisitsArrived: false,
            EndRouteOnAllVisitsDeparted: false,
            EndRouteOnArriveAtEndingLocation: false,
            EndRouteOnDepartAtEndingLocation: false,
            MinimumStartTime: null
        };

        this.setStartRulesFromPreferences(newRules);
        this.setEndRulesFromPreferences(newRules);

        return newRules;
    }

    setStartRulesFromPreferences(newRules: IRouteMatchingRules): void {
        newRules.StartRouteOnJourneyStart = false;
        newRules.StartRouteOnArriveAnyVisitLocation = false;
        newRules.StartRouteOnDepartAnyVisitLocation = false;
        newRules.StartRouteOnDepartStartingLocation = false;

        switch (this.preferences.RouteStartCondition.Value) {
            case RouteStartCondition.JourneyStart:
                newRules.StartRouteOnJourneyStart = true;
                break;
            case RouteStartCondition.DepartFromAnyVisit:
                newRules.StartRouteOnDepartAnyVisitLocation = true;
                break;
            case RouteStartCondition.DepartStartLocation:
                newRules.StartRouteOnDepartStartingLocation = true;
                break;
        }
    }

    setEndRulesFromPreferences(newRules: IRouteMatchingRules): void {
        newRules.EndRouteOnArriveAtEndingLocation = false;
        newRules.EndRouteOnAllVisitsArrived = false;
        newRules.EndRouteOnAllVisitsDeparted = false;
        newRules.EndRouteOnDepartAtEndingLocation = false;

        switch (this.preferences.RouteEndCondition.Value) {
            case RouteEndCondition.ArriveAllVisits:
                newRules.EndRouteOnAllVisitsArrived = true;
                break;
            case RouteEndCondition.ArriveEndLocation:
                newRules.EndRouteOnArriveAtEndingLocation = true;
                break;
        }
    }

    createDefaultVisitMatchingRules(): IVisitMatchingRules {
        const newRules: IVisitMatchingRules = {
            ArriveOn: this.preferences.VisitMatchCondition.Value,
            DepartOn: VisitMatchOptions.OnLocation,
            RecordUnplannedVisits: this.preferences.RecordUnplannedVisits.Value,
            CanMatchOnMissedVisits: this.preferences.CanMatchOnMissedVisits.Value,
            RecordVisitsAsMissedIfVisitedOutOfOrder: this.preferences.RecordVisitsAsMissedIfVisitedOutOfOrder.Value,
            ArriveOnTimeResidingInLocation: this.veriDate.convertMinsToTimeSpan(this.preferences.ArriveOnTimeResidingInLocation.Value, true),
            RestartRouteOnReturnToStartingLocation: this.preferences.RestartRouteOnReturnToStartingLocation.Value,
            MaximumTimeToRestartRoute: this.veriDate.convertMinsToTimeSpan(this.preferences.MaximumTimeToRestartRoute.Value, true),
        };

        return newRules;
    }

    createDefaultRouteExpiryRules(): IRouteExpiryRules {
        const newRules: IRouteExpiryRules = {
            MaximumLateTimeAllowed: this.veriDate.convertMinsToTimeSpan(this.preferences.MaximumLateTimeAllowed.Value, true),
            MaximumExpiryTime: null,
            MaximumStartTime: null
        };

        return newRules;
    }

    validateVisitList(visits: IPlannedRouteVisit[]): IPlannedRouteVisitListValidation {
        const validation: IPlannedRouteVisitListValidation = {
            valid: false,
            reasonMap: {}
        }

        if (visits?.filter(visit => visit.Status !== RouteVisitStatus.Cancelled)?.length < 2) {
            validation.reasonMap[PlannedRouteVisitListValidationReason.NoVisits] = true;

            return validation;
        }

        let previousLatLng: ILatLngLiteral;

        visits.forEach((visit, index) => {
            visit.validationError = null;
            visit.showFollowingDayLabel = false;

            const previousVisit = visits[index - 1];

            const plannedArrivalDate = new Date(visit.PlannedArrivalTime);
            const plannedArrivalDateString = this.veriDate.formatDate(plannedArrivalDate);
            const plannedArrivalTime = new Date(visit.PlannedArrivalTime).getTime();

            let previousVisitDepartureDateString;
            let previousVisitDepartureTime;

            if (previousVisit) {
                const previousVisitDepartureDate = new Date(previousVisit.PlannedDepartureTime);
                previousVisitDepartureDateString = this.veriDate.formatDate(previousVisitDepartureDate);
                previousVisitDepartureTime = new Date(previousVisit.PlannedDepartureTime).getTime();

                if (previousVisitDepartureTime > plannedArrivalTime) {
                    visit.validationError = this.translationService.t("SchedulingService.VisitValidationError_ArrivalTime", [plannedArrivalDateString, previousVisitDepartureDateString]);

                    validation.reasonMap[PlannedRouteVisitListValidationReason.VisitArrivalTimeBeforePrevious] = true;
                } else {
                    visit.showFollowingDayLabel = plannedArrivalDate.getDate() > previousVisitDepartureDate.getDate();
                }
            }

            if (!visit.RouteStopLocation) {
                previousLatLng = null;

                visit.validationError = this.translationService.t("SchedulingService.VisitValidationError_ValidLocation");

                validation.reasonMap[PlannedRouteVisitListValidationReason.VisitMissingLocation] = true;
                return;
            }

            if (previousLatLng
                && visit.RouteStopLocation.Latitude === previousLatLng.latitude
                && visit.RouteStopLocation.Longitude === previousLatLng.longitude) {
                visit.validationError = this.translationService.t("SchedulingService.VisitValidationError_SameLocation");

                validation.reasonMap[PlannedRouteVisitListValidationReason.VisitSameLocationAsPrevious] = true;
            }

            previousLatLng = {
                latitude: visit.RouteStopLocation.Latitude,
                longitude: visit.RouteStopLocation.Longitude
            };

            if (!visit.PlannedDepartureTime) {
                visit.validationError = this.translationService.t("SchedulingService.VisitValidationError_NoDepartureTime");

                validation.reasonMap[PlannedRouteVisitListValidationReason.NoDepartureTime] = true;
            }

            if (!visit.PlannedArrivalTime) {
                visit.validationError = this.translationService.t("SchedulingService.VisitValidationError_NoArrivalTime");

                validation.reasonMap[PlannedRouteVisitListValidationReason.NoArrivalTime] = true;
            }

            if (!visit.PlannedDepartureTime && !visit.PlannedArrivalTime) {
                visit.validationError = this.translationService.t("SchedulingService.VisitValidationError_NoArrivalAndDepartureTime");

                validation.reasonMap[PlannedRouteVisitListValidationReason.NoArrivalAndDepartureTime] = true;
            }
        });

        const totalErroredVisits = visits.filter(visit => !!visit.validationError)?.length;

        validation.valid = totalErroredVisits === 0;

        return validation;
    }

    setVisitsAsDirty(visits: IPlannedRouteVisit[]): void {
        visits?.forEach(visit => this.setVisitAsDirty(visit));
    }

    setVisitAsDirty(visit: IPlannedRouteVisit): void {
        visit.showValidationError = true;
    }

    setVisitAsNotDirty(visit: IPlannedRouteVisit): void {
        visit.showValidationError = false;
    }

    getActiveStartOptionProperty(rules: ITmsRouteRules): string {
        if (!rules?.RouteMatchingRules) {
            return;
        }

        const startOptions = Object.keys(this.getStartRouteMatchingRuleLabels());
        let activeStartOptionProperty;

        for (let i = 0; i < startOptions.length; i++) {
            const property = startOptions[i];

            if (rules.RouteMatchingRules[property]) {
                activeStartOptionProperty = property;
                break;
            }
        }

        return activeStartOptionProperty;
    }

    getActiveEndOptionProperty(rules: ITmsRouteRules): string {
        if (!rules?.RouteMatchingRules) {
            return;
        }

        const endOptions = Object.keys(this.getEndRouteMatchingRuleLabels());
        let activeEndOptionProperty: string;

        for (let i = 0; i < endOptions.length; i++) {
            const property = endOptions[i];

            if (rules.RouteMatchingRules[property]) {
                activeEndOptionProperty = property;
                break;
            }
        }

        return activeEndOptionProperty;
    }

    createPolylineForVisit(visit: IPlannedRouteVisit, id: number = 0): IPolyline<IPlannedRouteVisit> {
        const lineColor = "#242527";
        const latLngs: google.maps.LatLng[] = [];

        visit.LinePoints?.forEach((latLng: number[]) => {
            latLngs.push(new google.maps.LatLng(latLng[0], latLng[1]));
        });

        const newPolyline = this.mapService.createPolyline(0, latLngs, lineColor, null, false);
        newPolyline.id = id;

        this.mapService.addDirectionArrows(newPolyline, lineColor);

        return newPolyline;
    }

    createPolylineForVisits(visits: IPlannedRouteVisit[]): IPolyline<IPlannedRouteVisit> {
        const lineColor = "#242527";
        const latLngs: google.maps.LatLng[] = [];

        visits.forEach((visit) => {
            visit.LinePoints?.forEach((latLng: number[]) => {
                latLngs.push(new google.maps.LatLng(latLng[0], latLng[1]));
            });
        });

        const newPolyline = this.mapService.createPolyline(0, latLngs, lineColor, null, false);
        this.mapService.addDirectionArrows(newPolyline, lineColor);

        return newPolyline;
    }

    createRouteTemplate(): IPlannedRoute {
        const plannedRouteTemplate: IPlannedRoute = {
            Id: -1,
            AccountId: null,
            RouteTemplateId: null,
            Name: null,
            VehicleId: null,
            DriverId: null,
            CreationDateTime: null,
            LastUpdateDateTime: null,
            CreatedBy: null,
            ExternalReference: null,
            ActualStartTime: null,
            StartPlotId: null,
            StartPlot: null,
            ActualEndTime: null,
            EndPlotId: null,
            EndPlot: null,
            PlannedStartTime: null,
            PlannedEndTime: null,
            Status: null,
            TrackingMode: null,
            PlannedVisits: [],
            PlannedRouteNotes: [],
            TmsRouteRulesJson: null,
            TmsRouteRules: null
        };
        return plannedRouteTemplate;
    }

    createPlannedVisitTemplate(): IPlannedRouteVisit {
        const plannedVisitTemplate: IPlannedRouteVisit = {
            Id: Math.floor(Math.random() * -1000),
            RouteVisitPosition: null,
            Geofence: null,
            GeocodedAddress: null,
            TravelTime: null,
            TravelTimeBuffer: this.veriDate.convertMinsToTimeSpan(this.preferences.TravelBufferTime.Value, true),
            OnSiteTimeBuffer: this.veriDate.convertMinsToTimeSpan(this.preferences.OnSiteBufferTime.Value, true),
            TravelDistanceMetres: null,
            PointsEncoded: null,
            LinePoints: null,
            PlannedRouteId: null,
            ArrivalPlot: null,
            DeparturePlot: null,
            ActualDistanceTraveled: null,
            PlannedArrivalTime: null,
            PlannedDepartureTime: null,
            Status: null,
            RouteStopLocationJson: null,
            RouteStopLocation: null,
            Name: null,
            Address: null,
            OnSiteTime: "00:05:00",
            onSiteTimeMins: 5
        };

        return plannedVisitTemplate;
    }

    createHistoryTableHeaders(): IVeriTableHeader[] {
        return [
            this.veriTableService.createDateHeader("Start"),
            {
                title: this.translationService.t("SchedulingService.Route"),
                sortType: SortType.String,
                sortable: true
            },
            {
                title: this.translationService.t("Common.DriverVehicle"),
                sortType: SortType.String,
                sortable: true
            },
            {
                title: this.translationService.t("SchedulingService.EstDuration"),
                width: 120,
                sortType: SortType.Number,
                sortable: true
            },
            {
                title: this.translationService.t("SchedulingService.Visits"),
                width: 50,
                sortType: SortType.Number,
                sortable: true
            },
            {
                title: this.translationService.t("SchedulingService.Distance"),
                width: 80,
                sortType: SortType.Number,
                sortable: true
            },
            {
                title: this.translationService.t("SchedulingService.Status"),
                width: 130,
                sortType: SortType.String,
                sortable: true,
                includeInSummary: true
            }
        ];
    }

    createHistoryTableRows(routes: IPlannedRouteDetail[]): IVeriTableRow<IPlannedRouteDetail>[] {
        const tableRows: IVeriTableRow<IPlannedRouteDetail>[] = [];

        routes.forEach(route => {
            tableRows.push({
                data: route,
                cells: this.createTableCells(route),
                isInGroup: (data, group) => {
                    return group.VehicleIds.indexOf(data.VehicleId) >= 0 || group.DriverIds.indexOf(data.DriverId) >= 0;
                },
                searchString: `${route.Name}${route.vehicle ? route.vehicle.ShortReg + route.vehicle.Reg : ""}${route.driver ? route.driver.Name : ""}${this.veriDate.formatDate(route.PlannedStartTime)}`
            });
        });

        return tableRows;
    }

    fitMapToVisits(map: IVeriMapComponent, visits: IPlannedRouteVisit[]): void {
        if (!map || !visits?.length) {
            return;
        }

        const relavantVisits = visits.filter(visit => visit.RouteStopLocation?.Latitude && visit.RouteStopLocation?.Longitude);
        const latLngs: google.maps.LatLng[] = [];

        relavantVisits.forEach((visit) => {
            latLngs.push(new google.maps.LatLng(visit.RouteStopLocation.Latitude, visit.RouteStopLocation.Longitude));
        });

        if (!latLngs?.length) {
            return;
        }

        map.fitMap(latLngs);
    }

    // ---

    private createTableCells(route: IPlannedRouteDetail): IVeriTableRowCell[] {
        const cells: IVeriTableRowCell[] = [
            this.veriTableService.createDateTimeCell(route.PlannedStartTime),
            {
                contents: route.routeDisplayName
            },
            this.veriTableService.createDriverSummaryPanelCell(route.driver || route.firstVisit.driver, route.vehicle, null, route.firstVisit.Status == 0, "p-0"),
            { contents: this.veriDate.formatDurationMs(route.totalDurationMins * 60 * 1000), sortData: route.totalDurationMins },
            { contents: route.PlannedVisits.length.toString() },
            { contents: this.unitConversionService.convertUnitWithSuffix(route.totalPlannedDistanceKm, UnitConversionType.Distance) },
            { contents: route.routeDisplayStatus, summaryIcon: this.getTMSStatusIcon(route) }
        ];

        return cells;
    }

    private getTMSStatusIcon(route: IPlannedRoute): string {
        switch (route.Status) {
            case RouteStatus.Cancelled:

            case RouteStatus.Expired:
                return "highlight_off";

            case RouteStatus.Completed:
                return "check";

            case RouteStatus.CompletedWithMissing:
                return "rule";

            case RouteStatus.Planned:
                return "history_toggle_off";

            case RouteStatus.Incomplete:
                return "help_outline";
        }

        return "work";
    }
}
